'use client';

/**
 * NotificationInitializer Component
 *
 * Invisible component that initializes notification system:
 * - Registers service worker for Web Push
 * - Sets up Supabase Realtime subscription
 * - Plays notification sounds
 *
 * Place this in a layout or provider to initialize once.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAppSelector } from '@/store/hooks';
import logger from '@/lib/logger';
import { useServiceWorker } from '@/hooks/useServiceWorker';
import { useNotificationRealtime } from '@/hooks/useNotificationRealtime';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import { useNotifications } from '@/hooks/useNotifications';
import { selectPushSubscribed } from '@/store/slices/notificationsSlice';
import type { Notification } from '@/store/slices/notificationsTypes';

interface NotificationInitializerProps {
  /** User ID for realtime subscription */
  userId: string | null;
  /** Whether to enable notifications */
  enabled?: boolean;
}

export function NotificationInitializer({
  userId,
  enabled = true,
}: NotificationInitializerProps) {
  const isPushSubscribed = useAppSelector(selectPushSubscribed);
  const prevUnreadRef = useRef<number>(0);

  // Initialize service worker
  const { isRegistered, error: swError } = useServiceWorker({
    autoRegister: enabled,
  });

  // Sound playback
  const { playSound, isInQuietHours } = useNotificationSound({
    enabled: enabled,
  });

  // Handle new notification arrival - MUST be memoized to prevent infinite re-subscription loop
  const handleNewNotification = useCallback((notification: Notification) => {
    // Play sound if not in quiet hours and enabled
    if (!isInQuietHours && !notification.isRead) {
      // Use different sounds based on priority
      if (notification.priority === 'urgent' || notification.priority === 'high') {
        playSound('alert');
      } else {
        playSound('default');
      }
    }

    // Show in-app toast/snackbar could be added here
    logger.debug('NotificationInitializer', 'New notification received', { title: notification.title });
  }, [isInQuietHours, playSound]);

  // Subscribe to realtime notifications
  const { isSubscribed: isRealtimeSubscribed } = useNotificationRealtime({
    userId,
    enabled: enabled && !!userId,
    onNotification: handleNewNotification,
  });

  // Fallback: poll notifications when realtime is unavailable
  const { refresh } = useNotifications({ autoFetch: false });

  useEffect(() => {
    if (!enabled || !userId || isRealtimeSubscribed) {
      return;
    }

    const interval = window.setInterval(() => {
      refresh();
    }, 30000);

    return () => {
      window.clearInterval(interval);
    };
  }, [enabled, userId, isRealtimeSubscribed, refresh]);

  // Log initialization status
  useEffect(() => {
    if (enabled) {
      logger.debug('NotificationInitializer', 'Initializing', {
        userId,
        serviceWorker: isRegistered ? 'registered' : 'pending',
        realtime: isRealtimeSubscribed ? 'subscribed' : 'pending',
        pushSubscribed: isPushSubscribed,
      });
    }
  }, [enabled, userId, isRegistered, isRealtimeSubscribed, isPushSubscribed]);

  // Log errors
  useEffect(() => {
    if (swError) {
      logger.error('NotificationInitializer', 'Service worker error', { error: swError });
    }
  }, [swError]);

  // This component doesn't render anything visible
  return null;
}

export default NotificationInitializer;
